import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@mui/material';
import type { Point2D } from '@shared/models/geometry.model';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { formatCalibrationMeters } from '@shared/helpers/design/blueprint-calibration.helpers';
import type { BlueprintCalibrateLengthChrome } from './BlueprintCalibrateLengthChip';
type BlueprintCalibrateLengthPopupProps = {
  /** Midpoint of the calibration span in map overlay pixels. */
  screenPoint: Point2D;
  /**
   * Identity of the current measure session. Resets the field only when a new
   * pair of points is placed — not when the map pans/zooms.
   */
  sessionKey: string;
  /** Called with a positive length in meters. */
  onSubmit: (meters: number) => void;
  /**
   * @deprecated Prefer rendering outside the zoomed stage (HUD). Inverse scale
   * pixelates text when zoomed.
   */
  stageScale?: number;
  /** @deprecated Unused — kept for call-site compatibility. */
  chrome?: BlueprintCalibrateLengthChrome;
  /** Prefill when editing an already-committed known length. */
  initialMeters?: number;
  /** When false, do not auto-focus (e.g. committed chip that stays visible). */
  autoFocus?: boolean;
};

/**
 * Length entry under the calibration line — labeled field for known meters.
 */
export const BlueprintCalibrateLengthPopup: React.FC<
  BlueprintCalibrateLengthPopupProps
> = ({
  screenPoint,
  sessionKey,
  onSubmit,
  stageScale = 1,
  initialMeters,
  autoFocus = true,
}) => {
  const { classes } = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(() =>
    initialMeters !== undefined && initialMeters > 0
      ? formatCalibrationMeters(initialMeters)
      : '',
  );
  const inverse = stageScale > 0 ? 1 / stageScale : 1;

  // Reset when the measure span identity changes (not on pan/zoom).
  useEffect(() => {
    setValue(
      initialMeters !== undefined && initialMeters > 0
        ? formatCalibrationMeters(initialMeters)
        : '',
    );
    if (!autoFocus) return;
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(frame);
    // Only re-seed when the span session changes; length sync is below.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialMeters synced in the next effect
  }, [sessionKey, autoFocus]);

  // Keep the field in sync when length is edited elsewhere (e.g. props panel).
  useEffect(() => {
    if (initialMeters === undefined || !(initialMeters > 0)) return;
    if (document.activeElement === inputRef.current) return;
    setValue(formatCalibrationMeters(initialMeters));
  }, [initialMeters]);

  /**
   * Parse and submit a positive meter length.
   * @returns whether a valid length was submitted
   */
  const trySubmit = (): boolean => {
    const normalized = value.replace(',', '.').trim();
    const meters = Number(normalized);
    if (!(meters > 0) || !Number.isFinite(meters)) return false;
    onSubmit(meters);
    return true;
  };

  /**
   * Revert the field when the typed value is not a valid positive length.
   */
  const revertInvalidValue = () => {
    if (initialMeters !== undefined && initialMeters > 0) {
      setValue(formatCalibrationMeters(initialMeters));
      return;
    }
    setValue('');
  };

  const inputWidthCh = Math.max(4, Math.min(8, value.length || 4));

  return (
    <div
      className={classes.root}
      style={{
        left: screenPoint[0],
        top: screenPoint[1],
        // Inverse of parent stage scale keeps field size + 14px gap screen-constant.
        transform: `translate(-50%, 14px) scale(${inverse})`,
      }}
      onPointerDown={(event) => {
        // HUD sits inside the preview wrap — block measure/pan from stealing focus.
        event.stopPropagation();
      }}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
    >
      <div className={classes.card}>
        <Typography className={classes.label} component="span">
          Length:
        </Typography>
        <input
          ref={inputRef}
          className={classes.input}
          style={{ width: `${inputWidthCh}ch` }}
          value={value}
          inputMode="decimal"
          aria-label="Real distance in meters"
          onChange={(event) => setValue(event.target.value)}
          onBlur={() => {
            if (!trySubmit()) revertInvalidValue();
          }}
          onKeyDown={(event) => {
            if (event.key === KeyboardKeys.ENTER) {
              event.preventDefault();
              event.stopPropagation();
              if (!trySubmit()) revertInvalidValue();
              inputRef.current?.blur();
              return;
            }
            if (event.key === KeyboardKeys.ESCAPE) {
              // Let DesignMap’s Escape cascade run: 1) clear points, 2) draw tool.
              event.preventDefault();
              revertInvalidValue();
              inputRef.current?.blur();
            }
          }}
        />
        <Typography className={classes.unit} component="span">
          m
        </Typography>
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    position: 'absolute',
    zIndex: 3,
    pointerEvents: 'auto',
    // Anchor below the line midpoint; callers may override for inverse stage scale.
    transform: 'translate(-50%, 14px)',
    transformOrigin: 'top center',
  },
  card: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 32,
    padding: '4px 8px',
    borderRadius: 8,
    border: '1px solid rgba(0, 0, 0, 0.06)',
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box',
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: 15,
    fontWeight: 400,
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
  },
  input: {
    margin: 0,
    padding: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: theme.palette.text.primary,
    fontSize: 15,
    fontWeight: 400,
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'left',
    lineHeight: 1.4,
    minWidth: '4ch',
  },
  unit: {
    color: theme.palette.text.secondary,
    fontSize: 15,
    fontWeight: 400,
    lineHeight: 1.4,
  },
}));
